import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentModule } from '../contexts/ModuleContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart,
  LabelList
} from 'recharts';
import {
  TrendingUp,
  DollarSign,
  CheckCircle,
  Ticket,
  BarChart3,
  PieChartIcon,
  Users,
  RefreshCw,
  Star,
  MapPin,
  ChevronDown,
  X,
  Filter
} from 'lucide-react';
import { DateHierarchyFilter } from '../components/DateHierarchyFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TICKET_LOCATIONS = [
  "Mumbai", "Vankas (U1)", "Malkhet (U2)", "Kolkata", "Delhi",
  "Bangalore", "Chennai", "Pune", "Ahmedabad", "Hyderabad"
];

// Color palettes - Light aesthetic colors
const STATUS_COLORS = {
  'Open': '#60a5fa',      // Light blue
  'Approved': '#4ade80',  // Light green
  'In Progress': '#fbbf24', // Light amber
  'Completed': '#34d399', // Light emerald
  'Rejected': '#f87171'   // Light red
};

const CHART_COLORS = ['#93c5fd', '#86efac', '#fcd34d', '#fca5a5', '#c4b5fd', '#f9a8d4', '#67e8f9', '#bef264'];

const PRIORITY_COLORS = {
  'High': '#f87171',      // Light red
  'Medium': '#fbbf24',    // Light amber
  'Low': '#4ade80'        // Light green
};

// Light colors for expense charts
const EXPENSE_COLORS = {
  requestType: '#fcd34d',   // Light amber for request type
  department: '#86efac'     // Light green for department
};

export default function AnalyticsPage() {
  const { getAuthHeader, employee } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState(null);
  
  // Date range state - driven by DateHierarchyFilter
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  
  // Trend period filter state
  const [trendPeriod, setTrendPeriod] = useState('month');
  
  // Location filter state (multi-select)
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationStats, setLocationStats] = useState(null);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  // Request Type + Priority multi-select filters
  const [selectedRequestTypes, setSelectedRequestTypes] = useState([]);
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [rtDropdownOpen, setRtDropdownOpen] = useState(false);
  const [priorityDropdownOpen, setPriorityDropdownOpen] = useState(false);

  // Module filter state (Admin / IT) — defaults to IT for IT roles
  const _role = employee?.role || '';
  const _isITPrimary = _role === 'IT Team' || _role === 'IT Head';
  const [moduleFilter, setModuleFilter] = useState(_isITPrimary ? 'IT' : 'Admin');

  // Sync moduleFilter -> global header
  const { setCurrentModule } = useCurrentModule();
  useEffect(() => { setCurrentModule(moduleFilter); }, [moduleFilter, setCurrentModule]);

  const fetchAnalytics = useCallback(async (startStr, endStr, period = 'month', locations = [], moduleVal = 'all', requestTypes = [], priorities = []) => {
    setLoading(true);
    try {
      let url = `${API}/analytics/overview`;
      const params = new URLSearchParams();
      if (startStr) {
        params.append('start_date', startStr);
      }
      if (endStr) {
        params.append('end_date', endStr);
      }
      params.append('trend_period', period);
      if (locations.length > 0) {
        params.append('locations', locations.join(','));
      }
      if (moduleVal && (moduleVal === 'Admin' || moduleVal === 'IT')) {
        params.append('module', moduleVal);
      }
      if (requestTypes && requestTypes.length > 0) {
        params.append('request_type', requestTypes.join(','));
      }
      if (priorities && priorities.length > 0) {
        params.append('priority', priorities.join(','));
      }
      url += `?${params.toString()}`;

      const res = await fetch(url, {
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
        setError(null);
      } else if (res.status === 403) {
        setError('Access restricted: contact your administrator');
      } else {
        setError('Failed to load analytics data');
      }
    } catch (err) {
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchAnalytics(dateFrom, dateTo, trendPeriod, selectedLocations, moduleFilter, selectedRequestTypes, selectedPriorities);
  }, [fetchAnalytics, dateFrom, dateTo, trendPeriod, selectedLocations, moduleFilter, selectedRequestTypes, selectedPriorities]);

  // Fetch location stats
  const fetchLocationStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/dashboard/location-stats`, {
        headers: getAuthHeader(),
        
      });
      if (res.ok) {
        const data = await res.json();
        setLocationStats(data);
      }
    } catch (err) {
      console.error('Location stats error:', err);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchLocationStats();
  }, [fetchLocationStats]);

  // Toggle location selection
  const toggleLocation = (loc) => {
    setSelectedLocations(prev => 
      prev.includes(loc) ? prev.filter(l => l !== loc) : [...prev, loc]
    );
  };

  // Filter location stats by selected locations
  const filteredLocationStats = locationStats ? {
    tickets_by_location: selectedLocations.length > 0 
      ? locationStats.tickets_by_location.filter(r => selectedLocations.includes(r.location))
      : locationStats.tickets_by_location,
    requests_by_location: selectedLocations.length > 0
      ? locationStats.requests_by_location.filter(r => selectedLocations.includes(r.location))
      : locationStats.requests_by_location,
    expenses_by_location: selectedLocations.length > 0
      ? locationStats.expenses_by_location.filter(r => selectedLocations.includes(r.location))
      : locationStats.expenses_by_location,
  } : null;

  const role = employee?.role || 'Employee';

  // Get active filter label for subtitle
  const getActiveFilterLabel = () => {
    if (dateFrom && dateTo) {
      const from = new Date(dateFrom + 'T00:00:00');
      const to = new Date(dateTo + 'T00:00:00');
      return `${from.toLocaleDateString('en-IN', {day:'2-digit',month:'short',year:'numeric'})} - ${to.toLocaleDateString('en-IN', {day:'2-digit',month:'short',year:'numeric'})}`;
    }
    return null;
  };

  // Show loading state first
  if (loading) {
    return (
      <Layout>
        <div className="space-y-6" data-testid="analytics-page">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Analytics Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Comprehensive overview of ticket management metrics
            </p>
          </div>
          <div className="grid gap-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-8 w-16" />
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[1, 2].map(i => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <Skeleton className="h-[300px] w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // After loading, check for access restriction.
  // Allowed: Super Admin, Admin Head, Management, IT Head, IT Team
  if (!['Admin Head', 'Super Admin', 'Management', 'IT Head', 'IT Team'].includes(role)) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Card className="max-w-md">
            <CardContent className="p-8 text-center">
              <BarChart3 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
              <p className="text-muted-foreground">
                Analytics dashboard is only available to Admin Head, Super Admin, Management, IT Head and IT Team roles.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Card className="max-w-md">
            <CardContent className="p-8 text-center">
              <BarChart3 className="w-16 h-16 mx-auto text-red-500 mb-4" />
              <h2 className="text-xl font-semibold mb-2">Error</h2>
              <p className="text-muted-foreground">{error}</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="analytics-page">
        {/* Header with Date Filter */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Analytics Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Comprehensive overview of ticket management metrics
              {getActiveFilterLabel() && (
                <span className="ml-2 text-primary font-medium">
                  ({getActiveFilterLabel()})
                </span>
              )}
            </p>
          </div>
          
          {/* Date Filter Controls */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Hierarchical Date Filter */}
            <DateHierarchyFilter
              onDateChange={(from, to) => {
                setDateFrom(from);
                setDateTo(to);
              }}
            />

            {/* Location Multi-Select Dropdown */}
            <Popover open={locationDropdownOpen} onOpenChange={setLocationDropdownOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-[200px] justify-between text-left font-normal"
                  data-testid="location-filter-dropdown"
                >
                  <div className="flex items-center gap-2 truncate">
                    <MapPin className="h-4 w-4 shrink-0" />
                    <span className="truncate">
                      {selectedLocations.length === 0
                        ? 'All Locations'
                        : `${selectedLocations.length} selected`}
                    </span>
                  </div>
                  <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[220px] p-0" align="end">
                <div className="p-2 border-b">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Filter by Location</span>
                    {selectedLocations.length > 0 && (
                      <button
                        onClick={() => setSelectedLocations([])}
                        className="text-xs text-muted-foreground hover:text-foreground"
                        data-testid="clear-location-filter"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                </div>
                <div className="max-h-[240px] overflow-y-auto p-1">
                  {TICKET_LOCATIONS.map(loc => (
                    <label
                      key={loc}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-accent cursor-pointer text-sm"
                      data-testid={`location-filter-${loc.replace(/[() ]/g, '-')}`}
                    >
                      <Checkbox
                        checked={selectedLocations.includes(loc)}
                        onCheckedChange={() => toggleLocation(loc)}
                      />
                      <span>{loc}</span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* Refresh Button */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => fetchAnalytics(dateFrom, dateTo, trendPeriod, selectedLocations, moduleFilter, selectedRequestTypes, selectedPriorities)}
              disabled={loading}
              data-testid="refresh-analytics"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>

            {/* Request Type Multi-select */}
            <Popover open={rtDropdownOpen} onOpenChange={setRtDropdownOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start text-left" data-testid="rt-filter-trigger">
                  <Filter className="mr-2 h-4 w-4" />
                  Request Type
                  {selectedRequestTypes.length > 0 && (
                    <span className="ml-1.5 bg-primary text-primary-foreground text-xs rounded-full px-2 py-0.5">
                      {selectedRequestTypes.length}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0" align="end">
                <div className="p-2 border-b flex items-center justify-between">
                  <span className="text-sm font-medium">Request Types</span>
                  {selectedRequestTypes.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={() => setSelectedRequestTypes([])}>Clear</Button>
                  )}
                </div>
                <div className="max-h-64 overflow-y-auto p-2">
                  {(analytics?.tickets_by_type || []).map(rt => {
                    const name = rt.name || rt._id;
                    if (!name) return null;
                    const checked = selectedRequestTypes.includes(name);
                    return (
                      <label key={name} className="flex items-center gap-2 py-1 px-2 hover:bg-muted rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setSelectedRequestTypes(prev => checked ? prev.filter(x => x !== name) : [...prev, name])}
                          data-testid={`rt-filter-opt-${name}`}
                        />
                        <span className="text-sm flex-1 truncate" title={name}>{name}</span>
                        <span className="text-xs text-muted-foreground">{rt.count || rt.value}</span>
                      </label>
                    );
                  })}
                  {(!analytics?.tickets_by_type || analytics.tickets_by_type.length === 0) && (
                    <p className="text-xs text-muted-foreground p-2">No request types in current view</p>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {/* Priority Multi-select */}
            <Popover open={priorityDropdownOpen} onOpenChange={setPriorityDropdownOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start text-left" data-testid="priority-filter-trigger">
                  <Filter className="mr-2 h-4 w-4" />
                  Priority
                  {selectedPriorities.length > 0 && (
                    <span className="ml-1.5 bg-primary text-primary-foreground text-xs rounded-full px-2 py-0.5">
                      {selectedPriorities.length}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-0" align="end">
                <div className="p-2 border-b flex items-center justify-between">
                  <span className="text-sm font-medium">Priority</span>
                  {selectedPriorities.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={() => setSelectedPriorities([])}>Clear</Button>
                  )}
                </div>
                <div className="p-2 space-y-1">
                  {['High', 'Medium', 'Low'].map(p => {
                    const checked = selectedPriorities.includes(p);
                    return (
                      <label key={p} className="flex items-center gap-2 py-1 px-2 hover:bg-muted rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setSelectedPriorities(prev => checked ? prev.filter(x => x !== p) : [...prev, p])}
                          data-testid={`priority-filter-opt-${p.toLowerCase()}`}
                        />
                        <span className="text-sm">{p}</span>
                      </label>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Module Toggle (Admin / IT) */}
          <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="module-toggle">
            <span className="text-xs text-muted-foreground mr-1">Department:</span>
            {[
              { v: 'Admin', l: 'Admin' },
              { v: 'IT', l: 'IT' },
            ].map(opt => (
              <Button
                key={opt.v}
                size="sm"
                variant={moduleFilter === opt.v ? 'default' : 'outline'}
                onClick={() => setModuleFilter(opt.v)}
                data-testid={`module-toggle-${opt.v.toLowerCase()}`}
              >
                {opt.l}
              </Button>
            ))}
          </div>

          {/* Selected Location Badges */}
          {selectedLocations.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              {selectedLocations.map(loc => (
                <Badge key={loc} variant="secondary" className="text-xs gap-1">
                  {loc}
                  <button onClick={() => toggleLocation(loc)} className="ml-0.5 hover:text-foreground">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>

        {analytics && (
          <>
            {/* Summary KPI Cards - First Line */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 border-blue-200 dark:border-blue-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Total Tickets</p>
                      <p className="text-3xl font-bold text-blue-700 dark:text-blue-300 mt-1">
                        {analytics.summary.total_tickets.toLocaleString()}
                      </p>
                    </div>
                    <Ticket className="w-10 h-10 text-blue-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900 border-green-200 dark:border-green-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600 dark:text-green-400">Completion Rate</p>
                      <p className="text-3xl font-bold text-green-700 dark:text-green-300 mt-1">
                        {analytics.summary.completion_rate}%
                      </p>
                    </div>
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 border-purple-200 dark:border-purple-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Daily Expense Total</p>
                      <p className="text-2xl md:text-3xl font-bold text-purple-700 dark:text-purple-300 mt-1" data-testid="kpi-daily-expense-total">
                        ₹{(analytics.summary.daily_expense_total || 0).toLocaleString()}
                      </p>
                      <p className="text-[10px] text-purple-600/70 mt-1">Auto-updated by date filter</p>
                    </div>
                    <DollarSign className="w-10 h-10 text-purple-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950 dark:to-amber-900 border-amber-200 dark:border-amber-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-amber-600 dark:text-amber-400">Avg. Rating</p>
                      <p className="text-3xl font-bold text-amber-700 dark:text-amber-300 mt-1">
                        {analytics.summary.avg_rating ? analytics.summary.avg_rating.toFixed(1) : '0.0'} / 5
                      </p>
                    </div>
                    <Star className="w-10 h-10 text-amber-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* IT KPI: SLA Breaches (shown when IT department selected) */}
            {moduleFilter === 'IT' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="sla-section">
                <Card className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950 dark:to-red-900 border-red-200 dark:border-red-800">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-red-600 dark:text-red-400">SLA Breaches (IT)</p>
                        <p className="text-3xl font-bold text-red-700 dark:text-red-300 mt-1" data-testid="sla-breach-total">
                          {analytics.summary.sla_breach_total || 0}
                        </p>
                      </div>
                      <BarChart3 className="w-10 h-10 text-red-500" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" />
                      SLA Breaches by Person
                    </CardTitle>
                    <CardDescription>Tickets that breached the 4-working-hour SLA, grouped by original IT assignee</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {(analytics.sla_breaches_by_person?.length || 0) === 0 ? (
                      <p className="text-sm text-muted-foreground py-8 text-center">No SLA breaches in this period.</p>
                    ) : (
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={analytics.sla_breaches_by_person} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" allowDecimals={false} />
                          <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar dataKey="value" fill="#f87171" radius={[0, 4, 4, 0]}>
                            <LabelList dataKey="value" position="right" />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Tickets by Status - Pie Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChartIcon className="w-5 h-5" />
                    Tickets by Status
                  </CardTitle>
                  <CardDescription>Distribution of tickets across different statuses</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics.tickets_by_status.filter(item => item.name !== 'Draft')}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {analytics.tickets_by_status.filter(item => item.name !== 'Draft').map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={STATUS_COLORS[entry.name] || CHART_COLORS[index % CHART_COLORS.length]} 
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Tickets by Priority - Pie Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChartIcon className="w-5 h-5" />
                    Tickets by Priority
                  </CardTitle>
                  <CardDescription>Distribution of tickets by priority level</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics.tickets_by_priority}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {analytics.tickets_by_priority.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={PRIORITY_COLORS[entry.name] || CHART_COLORS[index % CHART_COLORS.length]} 
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Tickets by Rating - Bar Chart */}
            <Card data-testid="tickets-by-rating-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500" />
                  Tickets by Rating
                </CardTitle>
                <CardDescription>Number of completed tickets by user rating (1–5)</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics.tickets_by_rating || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" name="Tickets" radius={[6, 6, 0, 0]}>
                      {(analytics.tickets_by_rating || []).map((entry, index) => {
                        const colors = { 1: '#ef4444', 2: '#f97316', 3: '#eab308', 4: '#84cc16', 5: '#10b981' };
                        return <Cell key={`rating-cell-${index}`} fill={colors[entry.rating] || '#8884d8'} />;
                      })}
                      <LabelList dataKey="value" position="top" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Ticket Trend - Line Chart with Period Filter */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Ticket Trend
                    </CardTitle>
                    <CardDescription>Tickets created over time</CardDescription>
                  </div>
                  <Select value={trendPeriod} onValueChange={setTrendPeriod}>
                    <SelectTrigger className="w-32" data-testid="trend-period-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="week">Weekly</SelectItem>
                      <SelectItem value="month">Monthly</SelectItem>
                      <SelectItem value="quarter">Quarterly</SelectItem>
                      <SelectItem value="year">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={analytics.ticket_trend || []}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="period" className="text-xs" />
                    <YAxis className="text-xs" />
                    <Tooltip 
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-background border rounded-lg shadow-lg p-3">
                              <p className="font-medium">{label}</p>
                              {payload.map((entry, index) => (
                                <p key={index} style={{ color: entry.color }}>
                                  {entry.name}: {entry.value.toLocaleString()}
                                </p>
                              ))}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="created" 
                      name="Created"
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Charts Row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Tickets by Request Type - Bar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Tickets by Request Type
                  </CardTitle>
                  <CardDescription>Number of tickets per request type</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics.tickets_by_type} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" className="text-xs" />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        width={150} 
                        className="text-xs"
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" name="Tickets" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Tickets by Department - Bar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Top Departments
                  </CardTitle>
                  <CardDescription>Departments with most ticket requests</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics.tickets_by_department} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" className="text-xs" />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        width={150} 
                        className="text-xs"
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" name="Tickets" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Expense Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Expense by Request Type - Bar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Expense by Request Type
                  </CardTitle>
                  <CardDescription>Total amount spent per request type</CardDescription>
                </CardHeader>
                <CardContent>
                  {analytics.expense_by_request_type && analytics.expense_by_request_type.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={analytics.expense_by_request_type} layout="vertical" margin={{ right: 80 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis 
                          type="number" 
                          className="text-xs"
                          tickFormatter={(value) => `₹${value.toLocaleString()}`}
                        />
                        <YAxis 
                          dataKey="name" 
                          type="category" 
                          width={150} 
                          className="text-xs"
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip 
                          formatter={(value) => [`₹${value.toLocaleString()}`, 'Amount']}
                          contentStyle={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}
                        />
                        <Bar dataKey="value" name="Amount" fill="#fcd34d" radius={[0, 4, 4, 0]}>
                          <LabelList 
                            dataKey="value" 
                            position="right" 
                            formatter={(value) => `₹${value.toLocaleString()}`}
                            className="text-xs fill-foreground"
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                      No expense data available
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Expense by Department - Bar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Expense by Department
                  </CardTitle>
                  <CardDescription>Total amount spent per department</CardDescription>
                </CardHeader>
                <CardContent>
                  {analytics.expense_by_department && analytics.expense_by_department.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={analytics.expense_by_department} layout="vertical" margin={{ right: 80 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis 
                          type="number" 
                          className="text-xs"
                          tickFormatter={(value) => `₹${value.toLocaleString()}`}
                        />
                        <YAxis 
                          dataKey="name" 
                          type="category" 
                          width={150} 
                          className="text-xs"
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip 
                          formatter={(value) => [`₹${value.toLocaleString()}`, 'Amount']}
                          contentStyle={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}
                        />
                        <Bar dataKey="value" name="Amount" fill="#86efac" radius={[0, 4, 4, 0]}>
                          <LabelList 
                            dataKey="value" 
                            position="right" 
                            formatter={(value) => `₹${value.toLocaleString()}`}
                            className="text-xs fill-foreground"
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                      No expense data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Location Analytics Charts - Below Expense Charts */}
            {filteredLocationStats && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Tickets by Location */}
                <Card data-testid="tickets-by-location-chart">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Tickets by Location
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {filteredLocationStats.tickets_by_location?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={filteredLocationStats.tickets_by_location} layout="vertical" margin={{ left: 10, right: 30 }}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis type="category" dataKey="location" width={80} tick={{ fontSize: 10 }} />
                          <Tooltip formatter={(val) => [val, 'Tickets']} />
                          <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]}>
                            <LabelList dataKey="count" position="right" className="text-xs fill-foreground" />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">No location data yet</p>
                    )}
                  </CardContent>
                </Card>

                {/* Requests by Location */}
                <Card data-testid="requests-by-location-chart">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      Top Requests by Location
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {filteredLocationStats.requests_by_location?.length > 0 ? (
                      <div className="space-y-2 max-h-[260px] overflow-y-auto">
                        {filteredLocationStats.requests_by_location.slice(0, 12).map((item, i) => (
                          <div key={i} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-1.5 py-0.5 rounded whitespace-nowrap">
                                {item.location}
                              </span>
                              <span className="truncate text-muted-foreground">{item.request_type}</span>
                            </div>
                            <span className="font-semibold ml-2">{item.count}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">No request data yet</p>
                    )}
                  </CardContent>
                </Card>

                {/* Expenses by Location */}
                <Card data-testid="expenses-by-location-chart">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Expenses by Location
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {filteredLocationStats.expenses_by_location?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie
                            data={filteredLocationStats.expenses_by_location}
                            dataKey="total_expense"
                            nameKey="location"
                            cx="50%"
                            cy="50%"
                            outerRadius={75}
                            label={({ location, total_expense }) => `${location}: ₹${total_expense.toLocaleString()}`}
                            labelLine={false}
                          >
                            {filteredLocationStats.expenses_by_location.map((_, idx) => (
                              <Cell key={idx} fill={['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#f97316','#6366f1','#14b8a6'][idx % 10]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(val) => [`₹${val.toLocaleString()}`, 'Expense']} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">No expense data yet</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Top Requesters */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Top Requesters
                </CardTitle>
                <CardDescription>Employees with the most ticket requests</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {analytics.top_requesters.map((requester, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{requester.name}</p>
                        <div className="w-full bg-muted rounded-full h-2 mt-1">
                          <div 
                            className="bg-primary h-2 rounded-full transition-all duration-500"
                            style={{ 
                              width: `${(requester.tickets / analytics.top_requesters[0].tickets) * 100}%` 
                            }}
                          />
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="font-bold text-lg">{requester.tickets}</span>
                        <span className="text-muted-foreground text-sm ml-1">tickets</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
